import React, { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { StageDialogType, stageDialogConfig } from "@/config/stageDialogConfig"

interface ApplicationStageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: StageDialogType
  applicantName: string
  onConfirm: (data: { stage: string; reason: string; note: string }) => void
  isUpdating?: boolean
}

const ApplicationStageDialog: React.FC<ApplicationStageDialogProps> = ({
  open,
  onOpenChange,
  type,
  applicantName,
  onConfirm,
  isUpdating = false,
}) => {
  const config = stageDialogConfig[type]
  const [reason, setReason] = useState(config.reasons[0].value)
  const [note, setNote] = useState("")
  const requiresNote = reason === "other"
  const isNoteMissing = requiresNote && note.trim().length === 0

  const handleConfirm = () => {
    onConfirm({
      stage: config.stage,
      reason: reason,
      note: note,
    })
    // Don't reset state here as the parent component should handle dialog closing
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>
            {config.description.replace("{name}", applicantName)}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup value={reason} onValueChange={setReason}>
            {config.reasons.map((reasonOption) => (
              <div key={reasonOption.value} className="flex items-center space-x-2 mb-2">
                <RadioGroupItem value={reasonOption.value} id={`${type}-${reasonOption.value}`} />
                <Label htmlFor={`${type}-${reasonOption.value}`}>{reasonOption.label}</Label>
              </div>
            ))}
          </RadioGroup>

          <div className="mt-4">
            <Label htmlFor={`${type}-note`}>
              {requiresNote
                ? "Additional Notes (Required for Other)"
                : "Additional Notes (Optional)"}
            </Label>
            <Textarea
              id={`${type}-note`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional information here..."
              className="mt-1"
            />
            {requiresNote && (
              <p className="text-xs text-muted-foreground mt-1">
                Please provide a brief explanation when selecting Other.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isUpdating || isNoteMissing}>
            {isUpdating ? "Updating..." : config.confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ApplicationStageDialog
